import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  isStudyTypeClosed,
  useClosedStudyTypes,
} from "@/hooks/useClosedStudyTypes";
import {
  GOALS,
  isGoalIllustrationSet,
  type GoalDefinition,
  type GoalIllustrationSet,
} from "./goalsConfig";
import { GoalCard } from "./GoalCard";
import { StudySetupModal } from "./setup/StudySetupModal";
import {
  getResolvedGoalOrder,
  readGoalDashboardLayoutPreference,
} from "./dashboardLayoutPreference";
import { goalScreenStrings } from "./strings";
import {
  GOAL_CHIP_LAYOUT_ID,
  cardGridVariants,
  cardVariants,
  headlineVariants,
} from "./motion/tokens";
import type { GoalId, GoalIntakePayload } from "./types";
import type { Population } from "@/types/routes/tests";
import type { PopulationFilter } from "@/types/routes/test-results";
import type { Variants } from "framer-motion";

/**
 * GoalSelectionStep — the setup dashboard that opens study creation.
 *
 * Goals render in a uniform 4-column grid of classic study-type cards.
 */
interface GoalSelectionStepProps {
  onComplete: (goalId: GoalId, intake: GoalIntakePayload) => void;
  populations: Array<Population>;
  isAudienceLoading: boolean;
  selectedPopulationId: string;
  onSelectPopulation: (
    populationId: string,
    filter: PopulationFilter | null,
    filteredCount: number | null,
  ) => void;
}

interface GoalGridCardProps {
  goal: GoalDefinition;
  illustrationSet: GoalIllustrationSet;
  isSelected: boolean;
  /** True while the audience pool resolves — cards dim and reject clicks. */
  isSelectionDisabled: boolean;
  /** Full-study-only goal in quick mode — renders inert with "Coming soon". */
  locked: boolean;
  lockedLabel?: string;
  onSelect: (goalId: GoalId) => void;
  shouldReduceMotion: boolean;
}

function GoalGridCard({
  goal,
  illustrationSet,
  isSelected,
  isSelectionDisabled,
  locked,
  lockedLabel,
  onSelect,
  shouldReduceMotion,
}: GoalGridCardProps) {
  return (
    <div
      data-testid={`goal-grid-item-${goal.id}`}
      data-bento-size="standard"
      data-goal-category={goal.category}
      className="min-w-0 h-full"
    >
      <motion.div
        layoutId={
          isSelected && !shouldReduceMotion ? GOAL_CHIP_LAYOUT_ID : undefined
        }
        layout={!shouldReduceMotion}
        variants={shouldReduceMotion ? undefined : cardVariants}
        className="h-full"
      >
        {/*
          `locked` alone is enough — GoalCard forces its own dimmed state and
          disabled attr from it. The `|| locked` clauses below just keep this
          call site honest about what the card will actually render.
        */}
        <GoalCard
          goal={goal}
          state={
            isSelected
              ? "selected"
              : isSelectionDisabled || locked
                ? "dimmed"
                : "idle"
          }
          isSelectionDisabled={(isSelectionDisabled && !isSelected) || locked}
          locked={locked}
          lockedLabel={lockedLabel}
          onSelect={onSelect}
          illustrationSet={illustrationSet}
        />
      </motion.div>
    </div>
  );
}

const GOALS_BY_ID = GOALS.reduce<Record<GoalId, GoalDefinition>>((acc, goal) => {
  acc[goal.id] = goal;
  return acc;
}, {} as Record<GoalId, GoalDefinition>);

export function GoalSelectionStep({
  onComplete,
  populations,
  isAudienceLoading,
  selectedPopulationId,
  onSelectPopulation,
}: GoalSelectionStepProps) {
  const { t } = useTranslation("app");
  const shouldReduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const closedStudyTypes = useClosedStudyTypes();
  const artParam = searchParams.get("art");
  const illustrationSet: GoalIllustrationSet = isGoalIllustrationSet(artParam)
    ? artParam
    : "art";
  // Pending — not yet committed. Selecting a goal (or the inline audience
  // trigger) opens the setup modal; the goal only commits on "Continue".
  const [pendingGoalId, setPendingGoalId] = useState<GoalId | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [layoutPreference] = useState(readGoalDashboardLayoutPreference);

  const orderedGoalIds = useMemo(
    () => getResolvedGoalOrder(layoutPreference),
    [layoutPreference],
  );

  // Goal-card click opens the setup modal with that goal pending.
  // Guard against loading: populations aren't ready yet, so the modal would
  // show "No audiences yet." instead of the expected list.
  const handleSelectGoal = (goalId: GoalId) => {
    if (isAudienceLoading) return;
    if (isStudyTypeClosed(closedStudyTypes, goalId)) return;
    setPendingGoalId(goalId);
    setIsSetupOpen(true);
  };

  // Dismiss clears the pending goal so the grid returns to idle.
  const handleSetupOpenChange = (open: boolean) => {
    setIsSetupOpen(open);
    if (!open) setPendingGoalId(null);
  };

  // "Continue" commits the goal and advances to the questionnaire. Audience
  // and filter are already applied via their callbacks.
  const handleContinue = () => {
    if (!pendingGoalId) return;
    onComplete(pendingGoalId, {});
  };

  const pendingGoal = pendingGoalId ? GOALS_BY_ID[pendingGoalId] : null;

  function animated(variants: Variants) {
    if (shouldReduceMotion) return {};
    return { variants, initial: "hidden", animate: "visible" } as const;
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="goal-selection-step">
      <div className="mx-auto flex min-h-full w-full flex-col gap-4 py-3">
        <motion.div
          {...animated(headlineVariants)}
          className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="flex flex-col gap-1.5">
            <h1 className="font-display text-3xl font-normal leading-[1.15] tracking-[-0.02em] text-foreground dark:text-white">
              {goalScreenStrings.title}
            </h1>
            <p className="text-base text-muted-foreground">
              {goalScreenStrings.subtitle}
            </p>
          </div>
        </motion.div>

        <motion.div
          {...animated(cardGridVariants)}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 p-1"
          data-testid="goal-bento-grid"
          aria-label={t("creation.goalsAria")}
        >
          {orderedGoalIds.map((goalId) => {
            const goal = GOALS_BY_ID[goalId];
            const isSelected = goal.id === pendingGoalId;
            const isClosed = isStudyTypeClosed(closedStudyTypes, goal.id);
            const locked = isClosed;
            return (
              <GoalGridCard
                key={goal.id}
                goal={goal}
                illustrationSet={illustrationSet}
                isSelected={isSelected}
                isSelectionDisabled={isAudienceLoading}
                locked={locked}
                lockedLabel={
                  isClosed
                    ? goalScreenStrings.unavailable
                    : goalScreenStrings.comingSoon
                }
                onSelect={handleSelectGoal}
                shouldReduceMotion={shouldReduceMotion}
              />
            );
          })}
        </motion.div>

      </div>

      <StudySetupModal
        open={isSetupOpen}
        onOpenChange={handleSetupOpenChange}
        goal={pendingGoal}
        populations={populations}
        selectedPopulationId={selectedPopulationId}
        onSelectPopulation={onSelectPopulation}
        onContinue={handleContinue}
      />
    </div>
  );
}
